// src/components/classroom/liveKitMediaService.js
/**
 * ClassNX LiveKit Media Service
 * Manages media token retrieval, permissions validation, and attendance synchronization
 * strictly adhering to the backend security contract:
 * - NO API keys or secrets in frontend
 * - Short-lived tokens only
 * - Student media token requests require admitted guestToken
 * - Host media token requests require teacher JWT authorization
 */

import api from "../../api/httpClient";

export class MediaCredentialError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "MediaCredentialError";
    this.status = status;
    this.code = code;
  }
}

/**
 * Request media credentials from the Spring backend
 * Endpoint: POST /api/v2/classroom/rooms/{meetingCode}/media-token
 *
 * @param {Object} params
 * @param {string} params.meetingCode - Meeting ID / Room code
 * @param {string} [params.guestToken] - Admitted ClassNX guest token (mandatory for students)
 * @param {boolean} [params.isHost] - Whether caller is faculty/host
 * @returns {Promise<{ serverUrl: string, token: string, roomName: string, participantIdentity: string, participantName: string, role: string, host: boolean, allowedPublishSources: string[], expiresInSeconds: number }>}
 */
export async function requestMediaCredentials({ meetingCode, guestToken, isHost = false }) {
  if (!meetingCode) {
    throw new MediaCredentialError("Meeting code is required.", 400, "MISSING_MEETING_CODE");
  }

  const cleanCode = String(meetingCode).trim().toUpperCase();
  const endpoint = `/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/media-token`;

  const payload = {};
  if (!isHost) {
    if (!guestToken) {
      throw new MediaCredentialError(
        "Admission guest token is required to obtain media credentials.",
        401,
        "MISSING_GUEST_TOKEN"
      );
    }
    payload.guestToken = guestToken;
  }

  // Retrieve stored JWT if available for host authorization
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
  };

  if (isHost && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await api.post(endpoint, payload, { headers, skipAuth: !isHost });
    const responseData = res?.data || res;
    const mediaData = responseData?.data || responseData;

    if (!mediaData?.serverUrl || !mediaData?.token) {
      throw new MediaCredentialError(
        "Invalid media token payload received from server.",
        500,
        "INVALID_SERVER_RESPONSE"
      );
    }

    return {
      serverUrl: mediaData.serverUrl,
      token: mediaData.token,
      roomName: mediaData.roomName || `classnx-room-${cleanCode}`,
      participantIdentity: mediaData.participantIdentity || (isHost ? "host" : `guest:${guestToken}`),
      participantName: mediaData.participantName || (isHost ? "Faculty Host" : "Student"),
      role: mediaData.role || (isHost ? "TEACHER" : "STUDENT"),
      host: Boolean(mediaData.host ?? isHost),
      allowedPublishSources: Array.isArray(mediaData.allowedPublishSources)
        ? mediaData.allowedPublishSources
        : isHost
        ? ["camera", "microphone", "screen_share"]
        : ["microphone"],
      expiresInSeconds: mediaData.expiresInSeconds || 300,
    };
  } catch (err) {
    const status = err?.status || err?.response?.status || 500;
    const backendMessage =
      err?.response?.data?.message || err?.response?.data?.error || err?.message;

    if (status === 401) {
      throw new MediaCredentialError(
        "Your admission token has expired or is invalid. Please knock to enter again.",
        401,
        "TOKEN_EXPIRED"
      );
    }

    if (status === 403) {
      throw new MediaCredentialError(
        "Access denied. You are not authorized for this classroom session or tenant.",
        403,
        "UNAUTHORIZED_ACCESS"
      );
    }

    if (status === 404 || status === 410) {
      throw new MediaCredentialError(
        "This classroom is no longer live or has already ended.",
        status,
        "ROOM_NOT_LIVE"
      );
    }

    if (status === 500) {
      const customMsg = err?.response?.data?.message || err?.response?.data?.error;
      throw new MediaCredentialError(
        customMsg || "LiveKit media server environment is not configured on the backend.",
        500,
        "LIVEKIT_NOT_CONFIGURED"
      );
    }

    throw new MediaCredentialError(
      backendMessage || "Failed to establish media credentials.",
      status,
      "MEDIA_TOKEN_FAILED"
    );
  }
}

/**
 * Verify classroom room details & fetch student permissions
 * GET /api/v2/classroom/rooms/{meetingCode}
 */
export async function verifyClassroomRoom(meetingCode) {
  if (!meetingCode) return null;
  const cleanCode = String(meetingCode).trim().toUpperCase();

  try {
    const res = await api.get(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}`, { skipAuth: true });
    const data = res?.data?.data || res?.data || {};

    return {
      status: data.status || "live",
      topic: data.topic,
      subject: data.subject,
      teacherName: data.teacherName,
      allowStudentAudio: data.allowStudentAudio ?? true,
      allowStudentVideo: data.allowStudentVideo ?? false,
      scheduledDate: data.scheduledDate,
      startTime: data.startTime,
      endTime: data.endTime,
    };
  } catch {
    // If backend endpoint is unreachable, fallback to default permissions
    return {
      status: "live",
      allowStudentAudio: true,
      allowStudentVideo: false,
    };
  }
}

/**
 * Record attendance when leaving or disconnecting
 * Calls POST /api/v2/classroom/rooms/{meetingCode}/attendance
 */
export function recordClassroomAttendance(meetingCode, record, { keepalive = false } = {}) {
  if (!meetingCode || !record) return;
  const cleanCode = String(meetingCode).trim().toUpperCase();
  const endpoint = `/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/attendance`;

  if (keepalive && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(record)], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
      return;
    } catch {
      // Fall through to api.post
    }
  }

  try {
    api.post(endpoint, record, { keepalive: true, skipAuth: true }).catch(() => {});
  } catch {}
}
